import { useCallback, useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import { PublishRulesFormRef } from '../reducers/publishRulesReducer';
import { getPublishRulesIsNeedSaveAccept, getPublishRulesNextActiveTreeItem } from '../selectors/publishRulesSelectors';
import { IPublishRulesTreeItem } from '../interfaces/IPublishRulesTreeItem';
import {
  dropPublishRulesStateWithActiveTreeItemChange,
  updatePublishRulesActiveTreeItem,
} from '../actions/publishRulesActions';

interface IPublishRulesFormLeavingGuardProps<T extends PublishRulesFormRef> {
  formRef: T | undefined;
  nextActiveTreeItem: IPublishRulesTreeItem;
  isNeedSaveAccept: boolean;
}

export const PublishRulesFormLeavingGuardWithoutStore = <T extends PublishRulesFormRef>({
  formRef,
  nextActiveTreeItem,
  isNeedSaveAccept,
}: IPublishRulesFormLeavingGuardProps<T>) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [nextLocationPathname, setNextLocationPathname] = useState<string>();

  const closeModal = useCallback(() => setIsModalVisible(false), []);

  const showModal = (isFormValuesChanged: boolean, locationPathname?: string) => {
    locationPathname && setNextLocationPathname(locationPathname);
    isFormValuesChanged && !isFormChanged && setIsFormChanged(true);
    setIsModalVisible(true);
  };

  const getIsFormChanged = useCallback(() => {
    const formState = formRef?.current.getState();

    if (!formState?.pristine && formState?.valid) {
      return true;
    }

    return false;
  }, [formRef]);

  const handleBlockedNavigation = useCallback(
    ({ pathname }: { pathname: string }): boolean => {
      const isFormValuesChanged = getIsFormChanged();

      if (isFormValuesChanged) {
        showModal(isFormValuesChanged, pathname);

        return false;
      }

      return true;
    },
    [getIsFormChanged],
  );

  const handleModalApply = useCallback(() => {
    if (formRef?.current) {
      formRef.current.submit();
    }

    if (nextLocationPathname) {
      history.push(nextLocationPathname);
    }

    if (isNeedSaveAccept && nextActiveTreeItem) {
      dispatch(dropPublishRulesStateWithActiveTreeItemChange());
      dispatch(updatePublishRulesActiveTreeItem(nextActiveTreeItem));
    }

    closeModal();
    formRef?.current.restart();
  }, [closeModal, history, nextLocationPathname, formRef, isNeedSaveAccept, nextActiveTreeItem]);

  const handleModalClose = useCallback(() => {
    dispatch(dropPublishRulesStateWithActiveTreeItemChange());

    if (nextLocationPathname) {
      history.push(nextLocationPathname);
    }

    if (nextActiveTreeItem) {
      dispatch(updatePublishRulesActiveTreeItem(nextActiveTreeItem));
    }

    closeModal();
    formRef?.current.restart();
  }, [closeModal, nextActiveTreeItem, nextLocationPathname, formRef]);

  const handleUnload = useCallback(
    e => {
      const isFormValuesChanged = getIsFormChanged();

      if (isFormValuesChanged) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    [getIsFormChanged],
  );

  // Листенер закрытия вкладки браузера
  useEffect(() => {
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [handleUnload]);

  // Проверка смены текущего активного элемента дерева
  useEffect(() => {
    if (isNeedSaveAccept && nextActiveTreeItem) {
      const isFormValuesChanged = getIsFormChanged();

      if (isFormValuesChanged) {
        showModal(isFormValuesChanged);
      }
    }
  }, [isNeedSaveAccept]);

  return (
    <>
      <Prompt when={!isModalVisible} message={handleBlockedNavigation} />
      <ConfirmModal
        visible={isModalVisible}
        title="Подтверждение"
        text="Сохранить изменения настроек?"
        applyButtonText="Сохранить"
        cancelButtonText="Не сохранять"
        onApply={handleModalApply}
        onClose={handleModalClose}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  nextActiveTreeItem: getPublishRulesNextActiveTreeItem(state),
  isNeedSaveAccept: getPublishRulesIsNeedSaveAccept(state),
});

export const PublishRulesFormLeavingGuard = connect(mapStateToProps)(PublishRulesFormLeavingGuardWithoutStore);
